import { motion, AnimatePresence } from 'motion/react';
import { Check, Crown, Flame, Hourglass, WifiOff } from 'lucide-react';
import type { MemberView, ScoreLine } from '@gmp/shared-types/browser';
import { Avatar } from './Avatar';
import { usePartyUi } from './context';

export function PlayerChip({ member, size = 32, dim, children }: { member: MemberView | undefined; size?: number; dim?: boolean; children?: React.ReactNode }) {
  if (!member) return <span className="chip opacity-60">Former player</span>;
  return (
    <span className={`inline-flex items-center gap-2 min-w-0 ${dim ? 'opacity-50' : ''}`}>
      <Avatar id={member.avatar} size={size} color={member.color} />
      <span className="font-bold truncate">{member.nickname}</span>
      {member.isHost && <Crown size={14} className="text-brand-2 shrink-0" aria-label="host" />}
      {!member.connected && <WifiOff size={14} className="text-mute shrink-0" aria-label="reconnecting" />}
      {children}
    </span>
  );
}

/** Animated leaderboard: rows slide into their new order when scores change. */
export function Leaderboard({ scores, limit, compact, big }: { scores: ScoreLine[]; limit?: number; compact?: boolean; big?: boolean }) {
  const { member, me, reducedMotion } = usePartyUi();
  const rows = limit ? scores.slice(0, limit) : scores;
  return (
    <ol className="flex flex-col gap-2" aria-label="Scores">
      <AnimatePresence initial={false}>
        {rows.map((s) => {
          const m = member(s.playerId);
          return (
            <motion.li
              key={s.playerId}
              layout={!reducedMotion}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center gap-3 rounded-2xl px-3 ${compact ? 'py-1.5' : 'py-2.5'} ${s.playerId === me ? 'bg-ink-3 ring-2 ring-[var(--accent)]' : 'bg-ink-2'} ${big ? 'text-2xl' : ''}`}
            >
              <span className={`font-display font-black w-8 text-center ${s.rank === 1 ? 'text-brand-2' : 'text-mute'}`}>{s.rank}</span>
              {m ? <Avatar id={m.avatar} size={big ? 56 : compact ? 28 : 36} color={m.color} /> : <span className="w-9" />}
              <span className="font-bold truncate flex-1 min-w-0">{m?.nickname ?? 'Former player'}</span>
              {s.streak >= 3 && (
                <span className="chip text-[var(--accent-2)]" title={`${s.streak} scoring rounds in a row`}>
                  <Flame size={14} /> {s.streak}
                </span>
              )}
              {s.lastRound > 0 && <span className="text-good font-bold text-sm">+{s.lastRound}</span>}
              <span className="font-display font-black tabular-nums">{s.total.toLocaleString()}</span>
            </motion.li>
          );
        })}
      </AnimatePresence>
    </ol>
  );
}

/**
 * Pick-phase status line: "Waiting for 2 players…" (with their names) while people are still choosing,
 * then "Everyone's in, starting" once nobody is left (the server closes the phase a moment later).
 */
export function LockInStatus({ waiting, big }: { waiting: string[]; big?: boolean }) {
  const { member } = usePartyUi();
  const n = waiting.length;
  return (
    <p className={`flex items-center justify-center gap-1.5 text-center font-bold min-w-0 ${big ? 'text-2xl' : 'text-sm'}`} aria-live="polite" data-testid="lockin-status">
      {n === 0 ? (
        <><Check size={big ? 26 : 16} className="shrink-0 text-[var(--accent)]" aria-hidden /> Everyone’s in, starting</>
      ) : (
        <>
          <Hourglass size={big ? 24 : 14} className="shrink-0 text-mute" aria-hidden />
          <span className="shrink-0">Waiting for {n} player{n === 1 ? '' : 's'}…</span>
          <span className="text-mute font-normal truncate">{waiting.map((id) => member(id)?.nickname ?? '?').join(', ')}</span>
        </>
      )}
    </p>
  );
}

/**
 * LockInStatus for the viewer once they are in, worked out from who is done: the phase only waits for
 * connected people (bots answer the moment everyone is in), so they are the only ones listed.
 */
export function LockInWaiting({ players, done, big }: { players: string[]; done: string[]; big?: boolean }) {
  const { member, me } = usePartyUi();
  if (!me || !done.includes(me)) return null;
  const waiting = players.filter((id) => {
    const m = member(id);
    return !done.includes(id) && !!m && !m.bot && m.connected;
  });
  return <LockInStatus waiting={waiting} big={big} />;
}

/** Row of avatars showing who has locked in (never what they chose). */
export function LockInRow({ players, done, big }: { players: string[]; done: string[]; big?: boolean }) {
  const { member } = usePartyUi();
  return (
    <div className="flex flex-wrap justify-center gap-2" aria-label={`${done.length} of ${players.length} locked in`}>
      {players.map((id) => {
        const m = member(id);
        const ok = done.includes(id);
        return (
          <motion.div key={id} animate={{ scale: ok ? 1 : 0.85, opacity: ok ? 1 : 0.35 }} className="relative" title={m?.nickname}>
            {m && <Avatar id={m.avatar} size={big ? 64 : 36} color={ok ? m.color : '#3a2f55'} />}
          </motion.div>
        );
      })}
    </div>
  );
}
